import {
  type Component,
  createMemo,
  createResource,
  DEV,
  getOwner,
  onCleanup,
  type Resource,
  runWithOwner,
  Show,
  untrack,
} from "solid-js";
import type { RoutePreloadFunc, RouteSectionProps } from "@solidjs/router";
import type { FormVersion } from "../../state/formVersion";
import { FormVersionProvider } from "../../state/formVersion";
import type { Form } from "../../state/form";
import { FormProvider } from "../../state/form";
import FieldService from "../../services/FieldService";
import FormService from "../../services/FormService";
import ApiService from "../../services/ApiService";
import FormEditorContent, { FormEditorRoute } from "./FormEditorContent";
import { isNil } from "lodash-es";
import { toForm } from "../../utils/mappers/form";
import { toFormVersion } from "../../utils/mappers/formVersion";
import FormVersionService from "../../services/FormVersionService";
import ValidationService from "../../services/ValidationService";
import { ValidationProvider } from "../../state/validation";
import posthog from "posthog-js";
import FavouriteService from "../../services/FavouriteService";
import FormSettingsService from "../../services/FormSettingsService";
import RuleService from "../../services/RuleService";
import type { Intent } from "../../types/Router";
import { FormServicesProvider } from "../../services/formServices";
import FormServicesFactory from "../../services/FormServicesFactory";
import SubformService from "../../services/SubformService";
import NavigationService from "../../services/NavigationService";
import IntegrationService from "../../services/IntegrationService";
import PreviewService from "../../services/PreviewService";
import { detectMappingIssues } from "../../utils/mappers/mappingIssuesDetector";
import { Title } from "@solidjs/meta";
import { POSTHOG_EVENTS, POSTHOG_PROPERTIES } from "../../services/PostHogService";
import { detectDuplicateFieldIds, detectDuplicateSubformIds } from "../../utils/mappers/duplicateFieldIdsDetector";
import { useTranslation } from "../../state/i18n";
import FormEditorTour from "./FormEditorTour";
import IntegrationConfigurationService from "../../services/IntegrationConfigurationService";
import VariableService from "../../services/VariableService";
import FormId from "../../state/FormId";
import FormVersionId from "../../state/FormVersionId";
import CustomerId from "../../state/CustomerId";
import DataNameService from "../../services/DataNameService";
import { FormEditorTourProvider } from "./FormEditorTourProvider";
import GlobalLoadingPage from "../GlobalLoadingPage";
import { MAX_LAST_EDITED_FORMS, MINIMUM_PAGE_LOADING_DELAY } from "../../constants";
import { useLocalUserSettingsDispatch } from "../../state/localUserSettings.tsx";

export type FormEditorPageParams = {
  formId: string;
  formVersionId: string;
  customerId: string;
};

export type FormEditorPageProps = Resource<{ formVersion: FormVersion; form: Form }>;

const FormEditorPage: Component<RouteSectionProps<FormEditorPageProps | undefined>> = (props) => {
  const { t } = useTranslation();
  const setLocalUserSettings = useLocalUserSettingsDispatch();
  const editor = () => {
    const customerId = untrack(() => CustomerId.from(props.params.customerId!));
    const formId = untrack(() => FormId.from(props.params.formId!));
    FieldService.init();
    FormService.init();
    FormVersionService.init();
    PreviewService.init(customerId);
    SubformService.init();
    FormSettingsService.init();
    FavouriteService.init();
    RuleService.init();
    IntegrationService.init();
    VariableService.init();
    IntegrationConfigurationService.init();
    ValidationService.init();
    NavigationService.init();
    DataNameService.init();

    posthog.capture(POSTHOG_EVENTS.EDITOR_OPENED);

    posthog.register({
      [POSTHOG_PROPERTIES.FORM_VERSION_ID]: FormService.getFormVersionId(),
    });

    onCleanup(() => {
      posthog.unregister(POSTHOG_PROPERTIES.FORM_VERSION_ID);
    });

    setLocalUserSettings("lastEditedForms", (current) => {
      const existingForms = current?.[customerId.toString()] ?? [];
      return {
        ...current,
        [customerId.toString()]: [formId, ...existingForms.filter((id) => !id.equals(formId))].slice(
          0,
          MAX_LAST_EDITED_FORMS,
        ),
      };
    });

    return (
      <FormServicesProvider value={FormServicesFactory.forRootForm()}>
        <FormEditorTourProvider>
          <Title>{FormService.getForm().meta.name} | MoreApp</Title>
          <FormEditorTour />
          <FormEditorContent />
        </FormEditorTourProvider>
      </FormServicesProvider>
    );
  };

  return (
    <Show when={props.data?.()} fallback={<GlobalLoadingPage text={t(($) => $.FORM_EDITOR_LOADING)} />}>
      {(data) => (
        <FormProvider value={data().form}>
          <FormVersionProvider value={data().formVersion}>
            <ValidationProvider value={undefined}>{editor()}</ValidationProvider>
          </FormVersionProvider>
        </FormProvider>
      )}
    </Show>
  );
};

// Delay for given time, but only in production. Useful to avoid flickering when promises resolve too quickly
const minDelay = (delayInMs: number) =>
  new Promise<void>((resolve) => {
    isNil(DEV) ? setTimeout(resolve, delayInMs) : resolve();
  });

export const preLoad: RoutePreloadFunc<FormEditorPageProps | undefined> = (props) => {
  // Skip preloads for sideNav actions, since no new data need to be loaded for the formEditor at this point
  if (
    isSideNavPreload(
      untrack(() => props.intent),
      untrack(() => props.location.pathname),
    )
  ) {
    return;
  }

  const customerId = untrack(() => CustomerId.from(props.params.customerId!));
  const formId = createMemo(() => FormId.from(props.params.formId!));
  const formVersionId = createMemo(() => FormVersionId.from(props.params.formVersionId!));

  ApiService.init(customerId); // Currently has to be done here since 'CustomerLoader' does the init in the component and not in pre-load

  const owner = getOwner();

  const [resources] = createResource(async () => {
    const formVersionPromise = ApiService.getFormVersion(formId(), formVersionId());
    const formPromise = ApiService.getForm(formId());
    const [formVersionDto, formDto] = await Promise.all([
      formVersionPromise,
      formPromise,
      minDelay(MINIMUM_PAGE_LOADING_DELAY),
    ]);

    const publishedFormVersion = isNil(formDto.publishedVersion?.formVersion)
      ? undefined
      : await ApiService.getFormVersion(formId(), FormVersionId.from(formDto.publishedVersion.formVersion));
    const form = runWithOwner(owner, () => toForm(formDto, publishedFormVersion))!;
    const formVersion = runWithOwner(owner, () => toFormVersion(formVersionDto, true, form))!;

    detectMappingIssues(formVersionDto, formVersion);
    detectDuplicateFieldIds(formVersion);
    detectDuplicateSubformIds(formVersion);

    return { form, formVersion };
  });

  return resources;
};

const isSideNavPreload = (intent: Intent, pathname: string) =>
  intent === "preload" && Object.values(FormEditorRoute).some((route) => pathname.includes(route));

export default FormEditorPage;
